import { Link } from "react-router-dom";
import Langs from "../../utils/data/langs.json";
import { getImage } from "../../utils";
import YoutubeEmbed from "./youtube";
import { RatingStars } from "./ratingStars";

const getLangName = (key) => Langs.find((item) => item.value === key)?.name;

const viewProfileClassName =
    "block w-full rounded-xl bg-nt-blue px-4 py-2.5 text-center text-sm font-semibold text-white no-underline transition hover:bg-nt-blue/90";

const LangTag = ({ lang }) => {
    const name = getLangName(lang?.lang);
    if (!name || !lang?.level?.trim()) return null;

    return (
        <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-nt-blue">
            <span className="font-medium">{name}</span>
            <span className="text-nt-grey">·</span>
            <span className="text-nt-grey">{lang.level}</span>
        </span>
    );
};

const StatItem = ({ label, children }) => (
    <div className="rounded-lg border border-nt-grey-light bg-slate-50 px-3 py-2">
        <p className="text-xs font-medium text-nt-grey">{label}</p>
        <div className="mt-1 text-sm text-nt-blue">{children}</div>
    </div>
);

const TutorBrowseCard = ({ profile, isPublic = false }) => {
    const user = profile.userId || profile.user || {};
    const profileUrl = isPublic ? `/public/tutor/${profile._id}` : `/tutor/${profile._id}`;
    const teachLanguages = profile.teach || [];
    const speakLanguages = profile.speak || [];
    const price = teachLanguages[0]?.price;
    const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    const reviewCount = profile.globalReviews?.count ?? 0;
    const reviewStars = profile.globalReviews?.stars ?? 0;

    const statsGrid = (
        <div className="mt-2.5 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-2">
            <StatItem label={`Reviews (${reviewCount})`}>
                <RatingStars active={reviewStars} w="w-3.5" h="h-3.5" />
            </StatItem>
            <StatItem label="Lessons">
                <span className="font-semibold">{profile.lessons | 0}</span>
            </StatItem>
            <StatItem label="Students">
                <span className="font-semibold">{profile.students | 0}</span>
            </StatItem>
            <StatItem label="Attendance">
                <span className="font-semibold">
                    {profile.attendance && profile.attendance !== 0
                        ? `${profile.attendance}%`
                        : "100%"}
                </span>
            </StatItem>
        </div>
    );

    return (
        <article className="overflow-hidden rounded-2xl border border-nt-grey-light bg-white shadow-sm transition hover:border-nt-blue/25 hover:shadow-md">
            <div className="grid grid-cols-1 items-start md:grid-cols-[minmax(0,1fr)_minmax(280px,38%)] md:items-stretch">
                <div className="min-w-0 border-b border-nt-grey-light p-4 md:border-b-0 md:border-r md:p-5 flex flex-col justify-between">
                    <div className="flex gap-3">
                        <img
                            src={getImage(user?.avatar?.path, "AVATAR")}
                            alt=""
                            className="h-[72px] w-[72px] shrink-0 rounded-full object-cover ring-2 ring-slate-100"
                        />
                        <div className="min-w-0 flex-1 space-y-1.5">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <h3 className="text-lg font-semibold capitalize leading-tight text-nt-blue">
                                    {fullName || "Tutor"}
                                </h3>
                                {user?.country?.length ? (
                                    <img
                                        src={`https://flagsapi.com/${user.country}/shiny/24.png`}
                                        alt=""
                                        className="shrink-0"
                                    />
                                ) : null}
                            </div>
                            {price ? (
                                <p className="text-sm font-bold text-nt-blue">
                                    {price} € <span className="font-normal text-nt-grey">| Course</span>
                                </p>
                            ) : null}
                            {teachLanguages.length > 0 ? (
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <span className="shrink-0 text-xs text-nt-grey">Teaches:</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {teachLanguages.map((lang, index) => (
                                            <LangTag key={`teach-${lang.lang}-${index}`} lang={lang} />
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                            {speakLanguages.length > 0 ? (
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <span className="shrink-0 text-xs text-nt-grey">Speaks:</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {speakLanguages.map((lang, index) => (
                                            <LangTag key={`speak-${lang.lang}-${index}`} lang={lang} />
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {statsGrid}

                    <Link to={profileUrl} className={`${viewProfileClassName} mt-3 hidden md:block`}>
                        View Profile
                    </Link>
                </div>

                <div className="flex flex-col bg-slate-50 p-4 md:p-0">
                    {profile.youtube?.length ? (
                        <div className="aspect-video w-full overflow-hidden rounded-xl md:rounded-none md:h-full [&>*]:h-full [&_iframe]:h-full [&_iframe]:w-full">
                            <YoutubeEmbed url={profile.youtube} fill="container" />
                        </div>
                    ) : (
                        <div className="flex aspect-video w-full items-center justify-center rounded-xl border border-dashed border-nt-grey-light bg-white text-sm text-nt-grey md:aspect-auto md:h-full md:rounded-none md:border-0 md:border-l md:border-nt-grey-light">
                            No intro video
                        </div>
                    )}

                    <Link to={profileUrl} className={`${viewProfileClassName} mt-3 md:hidden`}>
                        View Profile
                    </Link>
                </div>
            </div>
        </article>
    );
};

export { TutorBrowseCard };
